/**
 * Parole: hashuire, verificare si politica.
 *
 * Sta in `core` pentru ca doua programe trebuie sa produca EXACT acelasi hash: Workerul care
 * verifica loginul si utilitarul de linie de comanda care seteaza prima parola de administrator.
 * Doua implementari care diverg cu o iteratie inseamna un cont care nu se mai poate autentifica si
 * o dupa-amiaza pierduta cautand de ce.
 *
 * ## Forma stocata
 *
 *     pbkdf2$sha256$<iteratii>$<sare-base64>$<hash-base64>
 *
 * Numarul de iteratii calatoreste IN hash, nu intr-o constanta din cod. Asta inseamna ca poate fi
 * crescut oricand — parolele vechi continua sa se verifice cu vechea valoare si se re-hashuiesc la
 * urmatorul login reusit. O constanta globala ar fi insemnat invalidarea tuturor parolelor la
 * fiecare ajustare.
 *
 * ## De ce doar 25.000 de iteratii, cand OWASP cere 600.000
 *
 * Pentru ca nu iteratiile sunt apararea principala aici, ci **piperul**.
 *
 * Masurat local: PBKDF2-HMAC-SHA256 costa ~0,26 ms la 1000 de iteratii, deci 600.000 ar fi ~160 ms
 * de CPU. Cloudflare Workers pe planul gratuit da ~10 ms de CPU per cerere. Nu incape, si nu e o
 * chestiune de optimizare.
 *
 * Asa ca inainte de PBKDF2 parola trece printr-un HMAC-SHA256 cu o cheie care traieste in Worker
 * secrets, niciodata in baza de date — exact regula de la `CREDENTIAL_KEY`. Consecinta: cine fura
 * un dump D1 NU poate ataca hashurile offline, oricat de mari ar avea dictionarul, pentru ca nu are
 * cheia. Iteratiile raman ca aparare de rezerva pentru cazul in care se scurg si baza, si cheia —
 * caz in care oricum sunt compromise si parolele bancare criptate cu `CREDENTIAL_KEY`.
 *
 * Ghicirea online e oprita in alta parte: blocare dupa 5 incercari gresite, in `login_attempt`.
 */

const CODIFICARE = new TextEncoder();

/** Valoarea implicita. Se poate ridica: fiecare hash isi poarta propriul numar. */
export const ITERATII_IMPLICITE = 25_000;

/** 16 octeti — recomandarea NIST pentru sarea PBKDF2. */
const OCTETI_SARE = 16;

/** 256 de biti, cat da SHA-256; mai mult nu adauga nimic la PBKDF2-HMAC-SHA256. */
const BITI_IESIRE = 256;

const PREFIX_ALGORITM = 'pbkdf2$sha256';

/**
 * Separarea de domeniu a piperului.
 *
 * `SIGNING_KEY` semneaza si URL-urile de atasament. Fara prefixul asta, un mesaj construit cu grija
 * ar putea produce aceeasi semnatura in doua scopuri diferite. Aceeasi tehnica e folosita de
 * `grantMessage` din `functions/api/lib/crypto.ts`.
 */
const DOMENIU_PIPER = 'parola\n';

function inBase64(octeti: Uint8Array): string {
  let binar = '';
  for (const octet of octeti) binar += String.fromCharCode(octet);
  return btoa(binar);
}

function dinBase64(text: string): Uint8Array {
  const binar = atob(text);
  const octeti = new Uint8Array(binar.length);
  for (let i = 0; i < binar.length; i++) octeti[i] = binar.charCodeAt(i);
  return octeti;
}

/**
 * Comparatie in timp constant.
 *
 * O comparatie normala de siruri iese la primul caracter diferit, deci timpul ei spune cate
 * caractere de la inceput erau corecte. Aici se compara toti octetii, mereu.
 */
export function comparaSigur(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diferenta = 0;
  for (let i = 0; i < a.length; i++) diferenta |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diferenta === 0;
}

/**
 * `ArrayBuffer` propriu, detasat de vederea originala.
 *
 * De ce nu se paseaza direct `Uint8Array`: din TypeScript 5.7, `Uint8Array` e generic peste tipul
 * bufferului, iar cel implicit (`ArrayBufferLike`) include `SharedArrayBuffer`, pe care
 * `BufferSource` nu-l accepta. Un `ArrayBuffer` simplu trece in orice configuratie de `lib`, si
 * fisierul asta e compilat in trei: Workers, DOM (SPA) si Node (teste, CLI).
 */
function caBuffer(octeti: Uint8Array): ArrayBuffer {
  return octeti.buffer.slice(
    octeti.byteOffset,
    octeti.byteOffset + octeti.byteLength,
  ) as ArrayBuffer;
}

/** Parola, trecuta prin piper. Rezultatul intra in PBKDF2 in locul parolei brute. */
async function piperuieste(parola: string, cheiePiper: string): Promise<Uint8Array> {
  const cheie = await crypto.subtle.importKey(
    'raw',
    caBuffer(CODIFICARE.encode(cheiePiper)),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const semnatura = await crypto.subtle.sign(
    'HMAC',
    cheie,
    caBuffer(CODIFICARE.encode(`${DOMENIU_PIPER}${parola}`)),
  );
  return new Uint8Array(semnatura);
}

async function deriva(
  parola: string,
  cheiePiper: string,
  sare: Uint8Array,
  iteratii: number,
): Promise<Uint8Array> {
  const piperata = await piperuieste(parola, cheiePiper);
  const material = await crypto.subtle.importKey('raw', caBuffer(piperata), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const biti = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: caBuffer(sare), iterations: iteratii },
    material,
    BITI_IESIRE,
  );
  return new Uint8Array(biti);
}

/**
 * Hashul de stocat pentru o parola noua.
 *
 * `cheiePiper` vine din Worker secrets (`SIGNING_KEY`). Rotirea ei invalideaza TOATE parolele, la
 * fel cum rotirea lui `CREDENTIAL_KEY` face indescifrabile secretele bancare — deci se roteste doar
 * deliberat, cu resetare de parole pregatita.
 */
export async function hashPassword(
  parola: string,
  cheiePiper: string,
  iteratii: number = ITERATII_IMPLICITE,
): Promise<string> {
  if (parola === '') throw new Error('Parola nu poate fi goala.');
  if (!Number.isInteger(iteratii) || iteratii < 1000) {
    throw new Error(`Numar de iteratii nerezonabil: ${iteratii}.`);
  }
  const sare = crypto.getRandomValues(new Uint8Array(OCTETI_SARE));
  const hash = await deriva(parola, cheiePiper, sare, iteratii);
  return `${PREFIX_ALGORITM}$${iteratii}$${inBase64(sare)}$${inBase64(hash)}`;
}

export interface HashDesfacut {
  iteratii: number;
  sare: Uint8Array;
  hash: string;
}

/** Desface forma stocata. `null` pentru orice nu e un hash valid — inclusiv `null` din baza. */
export function desfaHash(stocat: string | null | undefined): HashDesfacut | null {
  if (typeof stocat !== 'string') return null;
  const parti = stocat.split('$');
  // pbkdf2 | sha256 | iteratii | sare | hash
  if (parti.length !== 5) return null;
  if (parti[0] !== 'pbkdf2' || parti[1] !== 'sha256') return null;
  const iteratii = Number(parti[2]);
  if (!Number.isInteger(iteratii) || iteratii < 1000) return null;
  const sareB64 = parti[3];
  const hash = parti[4];
  if (sareB64 === undefined || hash === undefined || sareB64 === '' || hash === '') return null;
  try {
    return { iteratii, sare: dinBase64(sareB64), hash };
  } catch {
    return null;
  }
}

/**
 * Verifica o parola contra hashului stocat.
 *
 * Intoarce `false` — nu arunca — si pentru hash lipsa sau malformat: un utilizator fara parola
 * setata trebuie sa esueze la login exact ca unul cu parola gresita, nu cu alt mesaj si nu cu alt
 * timp de raspuns.
 */
export async function verifyPassword(
  parola: string,
  stocat: string | null | undefined,
  cheiePiper: string,
): Promise<boolean> {
  const desfacut = desfaHash(stocat);
  if (desfacut === null) return false;
  const calculat = await deriva(parola, cheiePiper, desfacut.sare, desfacut.iteratii);
  return comparaSigur(inBase64(calculat), desfacut.hash);
}

/** `true` daca hashul a fost creat cu mai putine iteratii decat cerem acum. */
export function trebuieRehashuit(
  stocat: string | null | undefined,
  iteratiiCurente: number = ITERATII_IMPLICITE,
): boolean {
  const desfacut = desfaHash(stocat);
  return desfacut !== null && desfacut.iteratii < iteratiiCurente;
}

// ---------------------------------------------------------------------------
// Politica
// ---------------------------------------------------------------------------

/**
 * Lungimea minima.
 *
 * 12, nu 8, si fara cerinte de „o majuscula si un simbol". Regulile de compozitie produc
 * `Parola1!` — care satisface orice bifa si cade la primul dictionar. Lungimea e singurul factor
 * care chiar conteaza, iar aici apara accesul la parolele bancare ale firmei.
 */
export const LUNGIME_MINIMA_PAROLA = 12;

/** Peste atat nu adauga securitate, dar ar consuma CPU degeaba la fiecare login. */
export const LUNGIME_MAXIMA_PAROLA = 200;

/**
 * Parolele pe care le vede oricine incearca primul. Lista e scurta deliberat: nu inlocuieste un
 * dictionar real, doar opreste alegerea evidenta facuta in graba la crearea unui cont.
 */
const PAROLE_INTERZISE: ReadonlySet<string> = new Set([
  'parola',
  'parolaparola',
  'password',
  'passwordpassword',
  '123456789012',
  'qwertyuiop12',
  'evomagevomag',
  'trezorerie12',
  'administrator',
]);

export interface RezultatPolitica {
  ok: boolean;
  motiv?: string;
}

export function politicaParola(parola: string): RezultatPolitica {
  if (parola.length < LUNGIME_MINIMA_PAROLA) {
    return { ok: false, motiv: `Parola trebuie sa aiba cel putin ${LUNGIME_MINIMA_PAROLA} caractere.` };
  }
  if (parola.length > LUNGIME_MAXIMA_PAROLA) {
    return { ok: false, motiv: `Parola nu poate depasi ${LUNGIME_MAXIMA_PAROLA} de caractere.` };
  }
  if (parola.trim() !== parola) {
    return { ok: false, motiv: 'Parola nu poate incepe sau se termina cu spatiu.' };
  }
  if (PAROLE_INTERZISE.has(parola.toLowerCase())) {
    return { ok: false, motiv: 'Parola aleasa e prea usor de ghicit.' };
  }
  // O parola dintr-un singur caracter repetat trece de lungime, dar nu de bun-simt.
  if (new Set(parola).size < 5) {
    return { ok: false, motiv: 'Parola foloseste prea putine caractere distincte.' };
  }
  return { ok: true };
}

/**
 * Parola initiala propusa de interfata cand un admin creeaza un cont.
 *
 * Alfabet fara caractere care se confunda la citit cu voce tare sau la transcris pe hartie
 * (`0`/`O`, `1`/`l`/`I`), pentru ca exact asta se intampla cu o parola initiala: se dicteaza.
 */
const ALFABET_GENERARE = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function generezaParola(lungime = 16): string {
  if (lungime < LUNGIME_MINIMA_PAROLA) lungime = LUNGIME_MINIMA_PAROLA;
  const octeti = crypto.getRandomValues(new Uint8Array(lungime));
  let iesire = '';
  // Alfabetul are 56 de caractere; 256 nu se imparte exact la 56, deci respingem octetii din coada
  // ca sa nu favorizam primele caractere. 256 - (256 % 56) = 256 - 32 = 224.
  const prag = 256 - (256 % ALFABET_GENERARE.length);
  let i = 0;
  while (iesire.length < lungime) {
    if (i >= octeti.length) {
      crypto.getRandomValues(octeti);
      i = 0;
    }
    const octet = octeti[i++] ?? 0;
    if (octet < prag) iesire += ALFABET_GENERARE[octet % ALFABET_GENERARE.length];
  }
  return iesire;
}
